import React, { useEffect, useState } from 'react';
import { Task } from '@models/task.model';
import { useGetMaterialAndEquipment } from '@hooks/api/material&Equipment/useGetMaterial&Equipment';
import { Card } from '@components/atoms/Card';
import { Badge } from '@components/atoms/Badge';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { useParams } from 'react-router-dom';

interface TaskDetailInventoryProps {
  task: Task | null;
}

export const TaskDetailInventory: React.FC<TaskDetailInventoryProps> = ({
  task,
}) => {
  const { id } = useParams<{ id: string }>();
  const { data } = useGetMaterialAndEquipment(id);
  const [inventory, setInventory] = useState<any>({
    materials: [],
    equipments: [],
  });
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    if (data?.materials || data?.equipments) {
      const materials = data?.materials || [];
      const equipments = data?.equipments || [];

      setInventory({
        materials,
        equipments,
      });
    }
  }, [data]);

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WarehouseOutlinedIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Inventory
          </h3>
          <Badge variant="secondary" size="sm">
            {inventory.materials?.length + inventory.equipments?.length}
          </Badge>
        </div>

        <div
          className="cursor-pointer p-1"
          onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? (
            <ExpandMoreOutlinedIcon />
          ) : (
            <ExpandLessOutlinedIcon />
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="space-y-6">
          {inventory.materials?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Materials
                </span>
                <Badge variant="primary" size="sm">
                  {inventory.materials?.length}
                </Badge>
              </div>

              <div className="bg-neutral-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20 overflow-hidden">
                <div className="divide-y divide-blue-100 dark:divide-blue-800/30 flex flex-col">
                  {inventory.materials?.map((m: any) => (
                    <div
                      key={m.materialId}
                      className="flex items-center justify-between p-4 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {m.name}
                          </div>
                          {task?.originalTaskId &&
                            m.taskId === task.originalTaskId && (
                              <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                                From original task
                              </div>
                            )}
                          {m.originalTaskId === task?.taskId &&
                            m.taskId !== task?.taskId && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                From delegated task{' '}
                                {m.chargeToClient ? '(billed to client)' : ''}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <Badge variant="primary" size="sm">
                          {m.amount} {m.amountType}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {inventory.equipments?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Equipment
                </span>
                <Badge variant="success" size="sm">
                  {inventory.equipments?.length}
                </Badge>
              </div>

              <div className="bg-neutral-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-900/20 overflow-hidden">
                <div className="divide-y divide-green-100 dark:divide-green-800/30 ">
                  {inventory.equipments?.map((e: any) => (
                    <div
                      key={e.equipmentId}
                      className="flex items-center justify-between p-4 hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {e.name}
                          </div>
                          {e.description && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
                              {e.description}
                            </div>
                          )}
                          {e.serialNumber && (
                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                              S/N: {e.serialNumber}
                            </div>
                          )}
                          {task?.originalTaskId &&
                            e.taskId === task.originalTaskId && (
                              <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                                From original task
                              </div>
                            )}
                          {e.originalTaskId === task?.taskId &&
                            e.taskId !== task?.taskId && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                From delegated task{' '}
                                {e.chargeToClient ? '(billed to client)' : ''}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <Badge variant="success" size="sm">
                          {e.amount}h
                        </Badge>
                        {e.hourlyRate && (
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            ${e.hourlyRate}/h
                          </span>
                        )}
                        {e.status && (
                          <Badge
                            variant={
                              e.status === 'available'
                                ? 'success'
                                : e.status === 'in-use'
                                  ? 'warning'
                                  : 'error'
                            }
                            size="sm">
                            {e.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default TaskDetailInventory;
